import {
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    AfterViewInit,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    HostBinding,
    ViewChild
} from '@angular/core';

import { BehaviorSubject, Subject, combineLatest, fromEvent, Observable } from 'rxjs';
import { first, filter, map, tap, takeUntil, switchMap } from 'rxjs/operators';

import { WorldConfigService } from '../world-config.service';

import { ScreepsRoomComponent } from '../screeps-room.component';

const ZOOM_LEVEL = 16;
const MIN_WIDTH = 1;
const MAX_WIDTH = 25;
const MIN_HEIGHT = 1;
const MAX_HEIGHT = 25;
const MIN_OFFSET = 1;
const MAX_OFFSET = 49;

const DEFAULT_FRAME = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
};

interface ISettings {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    rotation?: number;
    lighting?: boolean;
}

interface IFrame {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
}

@Component({
    selector: 'app-decoration-room-position',
    templateUrl: './decoration-room-position.component.pug',
    styleUrls: ['./decoration-room-position.component.less']
})
export class DecorationRoomPositionComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    _destroySbj: Subject<void> = new Subject();

    ZOOM_LEVEL = ZOOM_LEVEL;

    @ViewChild('frame', {static: false}) frameRef: ElementRef;
    @ViewChild('gameRoom', {static: false}) gameRoomRef: ScreepsRoomComponent;

    @HostBinding('class.--rotating') isRotating = false;
    @HostBinding('class.--proportional') get isProportional() {
        return this.decoration && this.decoration.props && this.decoration.props.proportional;
    }
    @HostBinding('class.--positionable') isPositionable = false;
    @HostBinding('class.--resizable') isResizable = false;
    @HostBinding('class.--rotatable') isRotatable = false;

    // _gameApp;
    @Input() shard: string;
    @Input() user: any;

    _roomSbj: BehaviorSubject<IRoom> = new BehaviorSubject(null);
    room$: Observable<IRoom> = this._roomSbj.asObservable();
    @Input() set room(room: IRoom) {
        this._roomSbj.next(room);
    }
    get room(): IRoom {
        return this._roomSbj.getValue();
    }

    _decoration;
    @Input() set decoration(decoration) {
        this._decoration = decoration;

        if (!decoration) {
            return;
        }

        const props = decoration.props;

        try {
            this.isPositionable = !props.x.readonly && !props.y.readonly;
        } catch (err) {
            this.isPositionable = false;
        }

        try {
            this.isResizable = !props.width.readonly && !props.height.readonly;
        } catch (err) {
            this.isResizable = false;
        }

        try {
            this.isRotatable = !props.rotation.readonly;
        } catch (err) {
            this.isRotatable = false;
        }
    }
    get decoration() {
        return this._decoration;
    }

    _decorationsSbj: BehaviorSubject<any[]> = new BehaviorSubject([]);
    decorations$: Observable<any[]> = this._decorationsSbj.asObservable();

    _inputDecorations;
    @Input() set decorations(decorations: any[]) {
        if (!decorations || !decorations.length) {
            return;
        }

        this._inputDecorations = decorations;
    }
    get decorations() {
        return this._inputDecorations;
    }

    _stateSbj: BehaviorSubject<IGameState> = new BehaviorSubject({});
    state$: Observable<IGameState> = this._stateSbj.asObservable();
    @Input() set state(state: IGameState) {
        this._stateSbj.next(state);
    }

    _settingsSbj: BehaviorSubject<ISettings> = new BehaviorSubject({});
    settings$ = this._settingsSbj.asObservable();
    @Input() set settings(settings) {
        this._settingsSbj.next(settings);
    }
    get settings() {
        return this._settingsSbj.getValue();
    }
    settingsRotation$ = this._settingsSbj.asObservable()
        .pipe(map(({ rotation }) => toDegree(rotation)));

    _frameSbj: BehaviorSubject<IFrame> = new BehaviorSubject({ ...DEFAULT_FRAME });
    frame$ = this._frameSbj.asObservable();
    get frame() {
        return this._frameSbj.getValue();
    }

    rotateDirectionDiameter$ = this._settingsSbj.asObservable()
        .pipe(map(({ width: w, height: h }) => Math.sqrt(w * w + h * h) * ZOOM_LEVEL));

    rotateDirectionTop$ = this.rotateDirectionDiameter$
        .pipe(map((d) => -(d - this.settings.height * ZOOM_LEVEL) / 2));

    rotateDirectionLeft$ = this.rotateDirectionDiameter$
        .pipe(map((d) => -(d - this.settings.width * ZOOM_LEVEL) / 2));


    @Output() change: EventEmitter<any> = new EventEmitter();

    constructor(
        private _ref: ElementRef,
        public worldConfig: WorldConfigService
    ) {
    }

    ngOnInit() {
        this.settings$
            .pipe(takeUntil(this._destroySbj))
            .pipe(map(calcFrameContour))
            .pipe(tap((frame) => this._frameSbj.next(frame)))
            .subscribe();
    }

    ngAfterViewInit() {
        this.decorations$
            .pipe(takeUntil(this._destroySbj))
            .pipe(filter((decorations) => decorations.some(({ decoration }) => ['wallLandscape', 'floorLandscape'].includes(decoration.type))))
            .pipe(switchMap(() => this.gameRoomRef.init()))
            .pipe(tap(() => this._stateSbj.next({ ...this._stateSbj.getValue() })))
            .subscribe();
    }

    async ngOnChanges() {
        const decorations = [...(this.decorations || []), {
            ...this.settings,
            user: this.user._id,
            decoration: {
                ...this.decoration,
                lighting: this.settings.lighting || false
            }
        }];

        this._decorationsSbj.next(decorations);

        if (['wallLandscape', 'floorLandscape', 'object'].includes(this.decoration.type)) {
            this._stateSbj.next({ ...this._stateSbj.getValue() });
        }
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }

    onStartMove($event) {
        const move$ = fromEvent(document.body, 'mousemove');
        const up$ = fromEvent(document.body, 'mouseup');
        const leave$ = fromEvent(this._ref.nativeElement, 'mouseleave');

        const settings = this.settings;

        const startX = $event.clientX;
        const startY = $event.clientY;

        const moveh$ = move$.pipe(takeUntil(up$)).pipe(takeUntil(leave$))
            .pipe(map<MouseEvent, any>(({ clientX }) => ({
                x: settings.x + (clientX - startX) / ZOOM_LEVEL,
                width: settings.width
            })))
            // Limits of resize by Ox
            .pipe(filter(validateHorizontalSettings(this.decoration.props)));

        const movev$ = move$.pipe(takeUntil(up$)).pipe(takeUntil(leave$))
            .pipe(map<MouseEvent, any>(({ clientY }) => ({
                y: settings.y + (clientY - startY) / ZOOM_LEVEL,
                height: settings.height
            })))
            // Limits of resize by Oy
            .pipe(filter(validateVerticalSettings(this.decoration.props)));

        combineLatest(moveh$, movev$)
            .pipe(map(([a, b]) => ({ ...a, ...b })))
            .pipe(tap((settings) => {
                this.settings = {
                    ...this.settings,
                    ...settings
                };
            }))
            .pipe(tap(() => this.change.emit(this.settings)))
            .subscribe();
    }

    onStartResize($event, type) {
        const move$ = fromEvent(document.body, 'mousemove');
        const up$ = fromEvent(document.body, 'mouseup');
        const leave$ = fromEvent(this._ref.nativeElement, 'mouseleave');

        const frame = this.frame;
        const settings = this.settings;

        const startX = $event.clientX;
        const startY = $event.clientY;

        move$.pipe(takeUntil(up$))
            .pipe(takeUntil(leave$))
            .pipe(map<MouseEvent, any>(({ clientX, clientY }) => {
                return {
                    clientX,
                    clientY,
                    startX: ['n', 's'].includes(type) ? clientX : startX,
                    startY: ['e', 'w'].includes(type) ? clientY : startY
                };
            }))
            .pipe(map(({ clientX, clientY, startX, startY }) => {
                const dx = clientX - startX;
                const dy = clientY - startY;

                let signOx, signOy;
                [signOx, signOy] = [1, 1];
                if (type === 'w') {
                    [signOx, signOy] = [-1, 1];
                }
                if (type === 'n') {
                    [signOx, signOy] = [-1, -1];
                }

                const W = (frame.width + signOx * dx) / ZOOM_LEVEL;
                const H = (frame.height + signOy * dy) / ZOOM_LEVEL;

                const [w, h] = calcSize(W, H, settings.rotation);

                const dw =  (w - settings.width) / 2;
                const nx = settings.x - dw + dx / ZOOM_LEVEL / 2;
                const dh =  (h - settings.height) / 2;
                const ny = settings.y - dh + dy / ZOOM_LEVEL / 2;

                return {
                    x: nx,
                    y: ny,
                    width: w,
                    height: h
                };
            }))
            // Limits of resize by Ox
            .pipe(filter(validateHorizontalSettings(this.decoration.props)))
            // @ts-ignore
            // Limits of resize by Oy
            .pipe(filter(validateVerticalSettings(this.decoration.props)))
            .pipe(tap((settings) => {
                this.settings = {
                    ...this.settings,
                    // @ts-ignore
                    ...settings
                };
            }))
            .pipe(tap(() => this.change.emit(this.settings)))
            .subscribe();

        $event.stopPropagation();
    }

    onStartProportionalResize($event, type) {
        const move$ = fromEvent(document.body, 'mousemove');
        const up$ = fromEvent(document.body, 'mouseup');
        const leave$ = fromEvent(this._ref.nativeElement, 'mouseleave');

        const frame = this.frame;
        const settings = this.settings;

        const startX = $event.clientX;
        const startY = $event.clientY;

        move$.pipe(takeUntil(up$))
            .pipe(takeUntil(leave$))
            // .pipe(map<MouseEvent, any>(({ movementX, movementY }) => {
            .pipe(map<MouseEvent, any>(({ clientX, clientY }) => {
                return {
                    clientX,
                    clientY,
                    startX,
                    startY
                };
            }))
            .pipe(map(({ clientX, clientY, startX, startY }) => {
                let dx = clientX - startX;
                let dy = clientY - startY;

                let signOx, signOy;
                [signOx, signOy] = [1, 1];
                if (type === 'ne') {
                    [signOx, signOy] = [1, -1];
                }
                if (type === 'sw') {
                    [signOx, signOy] = [-1, 1];
                }
                if (type === 'nw') {
                    [signOx, signOy] = [-1, -1];
                }

                let W = frame.width + signOx * dx;
                let H = frame.height + signOy * dy;

                const wratio = W / frame.width;
                const hratio = H / frame.height;

                const ratio = Math.min(wratio, hratio);

                W = (frame.width * ratio) / ZOOM_LEVEL;
                H = (frame.height * ratio) / ZOOM_LEVEL;

                dx = W - frame.width / ZOOM_LEVEL;
                dy = H - frame.height / ZOOM_LEVEL;

                const [w, h] = calcSize(W, H, settings.rotation);

                const dw =  (w - settings.width) / 2;
                const nx = settings.x - dw + signOx * dx / 2;
                const dh =  (h - settings.height) / 2;
                const ny = settings.y - dh + signOy * dy / 2;

                return {
                    x: nx,
                    y: ny,
                    width: w,
                    height: h
                };
            }))
            // Limits of resize by Ox
            .pipe(filter(validateHorizontalSettings(this.decoration.props)))
            // @ts-ignore
            // Limits of resize by Oy
            .pipe(filter(validateVerticalSettings(this.decoration.props)))
            .pipe(tap((settings) => {
                this.settings = {
                    ...this.settings,
                    // @ts-ignore
                    ...settings
                };
            }))
            .pipe(tap(() => this.change.emit(this.settings)))
            .subscribe();

        $event.stopPropagation();
    }

    onStartRotate($event) {
        this.isRotating = true;

        const move$ = fromEvent(document.body, 'mousemove');
        const up$ = fromEvent(document.body, 'mouseup');

        const { rotation } = this.settings;
        const { top, left, height, width } = this.frameRef.nativeElement.getBoundingClientRect();

        const ox = left + (width / 2);
        const oy = top + (height / 2);

        const mx = $event.clientX - ox;
        const my = $event.clientY - oy;

        const startAngle = Math.atan2(my, mx);

        move$.pipe(takeUntil(up$))
            .pipe(map<MouseEvent, any>(({ clientX, clientY }) => {
                const x = clientX - ox;
                const y = clientY - oy;
                const d = Math.atan2(y, x);
                const _rotation = d - startAngle;

                return _rotation;
            }))
            .pipe(tap((_rotation) => {
                this.settings = {
                    ...this.settings,
                    rotation: rotation + _rotation
                };
            }))
            .pipe(tap(() => this.change.emit(this.settings)))
            .subscribe();

        up$ .pipe(first())
            .pipe(tap(() => this.isRotating = false))
            .subscribe();

        $event.stopPropagation();
    }
}

function rotatePoint(x, y, radians) {
    const cx = 0, cy = 0;

    const cos = Math.cos(radians),
        sin = Math.sin(radians),
        nx = (cos * (x - cx)) + (sin * (y - cy)) + cx,
        ny = (cos * (y - cy)) - (sin * (x - cx)) + cy;

    return [nx, ny];
}

function validateHorizontalSettings({ maxWidth, minWidth }) {
    minWidth = minWidth || MIN_WIDTH;
    maxWidth = maxWidth || MAX_WIDTH;

    return function({ x, width }) {
        return !(width < minWidth || (x + width) < MIN_OFFSET || x > MAX_OFFSET || width > maxWidth);
    };
}

function validateVerticalSettings({ maxHeight, minHeight }) {
    minHeight = minHeight || MIN_HEIGHT;
    maxHeight = maxHeight || MAX_HEIGHT;

    return function({ y, height }) {
        return !(height < minHeight || (y + height) < MIN_OFFSET || y > MAX_OFFSET || height > maxHeight);
    };
}

function toRadians(angle) {
    return angle / 180 * Math.PI;
}

function toDegree(radians) {
    return radians * 180 / Math.PI;
}

function calcSize(W, H, rotation) {
    if (rotation < 0) {
        rotation += 2 * Math.PI;
    }

    const r = rotation % (Math.PI / 2);
    const degree = toDegree(rotation);

    const x = Math.sin(r);
    const y = Math.cos(r);
    const w = ((W + H) / (x + y) - (W - H) / (x - y)) / 2;
    const h = (W + H) / (x + y) - w;

    if ((degree >= 0 && degree < 90) || (degree >= 180 && degree < 270)) {
        return [w, h];
    }

    return [h, w];
}

function calcFrameContour({ width, height, rotation }) {
    const halfW = width / 2;
    const halfH = height / 2;

    const [x1, y1] = rotatePoint(-halfW,  halfH, rotation);
    const [x2, y2] = rotatePoint( halfW,  halfH, rotation);
    const [x3, y3] = rotatePoint( halfW, -halfH, rotation);
    const [x4, y4] = rotatePoint(-halfW, -halfH, rotation);

    const minX = Math.min(x1, x2, x3, x4);
    const minY = Math.min(y1, y2, y3, y4);
    const maxX = Math.max(x1, x2, x3, x4);
    const maxY = Math.max(y1, y2, y3, y4);

    return {
        x: (minX + halfW) * ZOOM_LEVEL,
        y: (minY + halfH) * ZOOM_LEVEL,
        width: (2 * maxX) * ZOOM_LEVEL,
        height: (2 * maxY) * ZOOM_LEVEL
    };
}
